/* ==========================================================================
   Rook UI — Flavor Showcase Controller
   Web Component: <rk-flavor-showcase>

   Cada slide do carousel é um sabor (lata + card). Este controlador:
     - marca o showcase como pronto (is-ready) → o CSS passa a mostrar só o card
       do slide ativo;
     - lê data-bg/data-fg/data-flavor do slide ATIVO e aplica a cor de fundo
       "por sabor" na seção (--rk-flavor-bg/--rk-flavor-fg, com transição CSS);
     - sincroniza os dots (1 por sabor) pelo realIndex (ignora clones do loop);
     - clique no dot centraliza aquele sabor.

   Degrade suave: sem JS / antes de hidratar, todos os cards aparecem (sem
   is-ready) e o fundo fica na cor do esquema.

   Hydration tier: 'visible' — alinhado ao carousel.
   ========================================================================== */

import { RkComponent } from '@rook/component';

class RkFlavorShowcase extends RkComponent {
  static hydration = 'visible';

  setup() {
    this.carousel = this.querySelector('rk-carousel-element');
    this.dots = Array.from(this.querySelectorAll('.rk-flavor-showcase__dot'));
    this.cards = Array.from(this.querySelectorAll('.rk-flavor-showcase__card'));
    this.live = this.querySelector('[data-rk-flavor-live]');
    // Seção pai: onde aplicamos a cor de fundo por sabor.
    this.section = this.closest('.shopify-section');
    this._bound = false;

    // Preenche os cards (preço + add-to-cart) a partir dos <template> dos slides,
    // antes do Swiper clonar os slides do loop. O produto só resolve no contexto
    // do bloco filho, então o card vem de lá.
    this.#fillCards();

    if (!this.carousel) return;

    // O evento rk:carousel:init borbulha do carousel filho até aqui.
    this._onInit = (event) => this.#bind(event.detail && event.detail.swiper);
    this.addEventListener('rk:carousel:init', this._onInit);
    if (this.carousel.swiper) this.#bind(this.carousel.swiper);

    // Clique nos dots e nas setas customizadas.
    this._onClick = (event) => {
      const dot = event.target.closest('.rk-flavor-showcase__dot');
      if (dot && this.contains(dot)) {
        this.#goToDot(Number(dot.dataset.dotIndex));
        return;
      }
      const arrow = event.target.closest('.rk-flavor-showcase__arrow');
      if (arrow && this.contains(arrow) && this._swiper) {
        if (arrow.dataset.arrow === 'prev') this._swiper.slidePrev();
        else this._swiper.slideNext();
      }
    };
    this.addEventListener('click', this._onClick);
  }

  // Preenche cada card-wrapper (vazio, vindo do bloco-pai) clonando o
  // <template> do slide correspondente (casado por data-flavor-key). O card de
  // produto é renderizado no contexto do bloco filho — único lugar onde a setting
  // de tipo recurso `product` resolve. Os custom elements nativos
  // (product-form-component etc.) hidratam ao serem inseridos no DOM.
  #fillCards() {
    const slides = Array.from(this.querySelectorAll('.rk-flavor-showcase__slide'));
    this.cards.forEach((card) => {
      if (card.childElementCount > 0) return; // já preenchido
      const key = card.dataset.flavorKey;
      if (!key) return;
      // O child.id (wrapper, no pai) e o block.id (slide, no filho) referem o
      // mesmo bloco, mas um lado pode vir com sufixo de instância (ex.: o wrapper
      // `...r8DYca-1` vs. o slide `...r8DYca`). Casamos quando um é prefixo do
      // outro na fronteira `-` — tolera a direção do sufixo e clones de loop.
      const slide = slides.find((s) => {
        const sk = s.dataset.flavorKey;
        return sk && (sk === key || sk.indexOf(key + '-') === 0 || key.indexOf(sk + '-') === 0);
      });
      const tpl = slide && slide.querySelector('.rk-flavor-showcase__card-template');
      if (tpl && tpl.content) card.appendChild(tpl.content.cloneNode(true));
    });
  }

  #bind(swiper) {
    if (!swiper || this._bound) return;
    this._bound = true;
    this._swiper = swiper;
    this.classList.add('is-ready');

    this._onSlideChange = () => this.#sync(swiper);
    swiper.on('slideChange', this._onSlideChange);
    this.#sync(swiper);
  }

  #sync(swiper) {
    const active = swiper.slides && swiper.slides[swiper.activeIndex];

    // Cor de fundo + texto a partir do slide ativo (clones carregam os data-*).
    if (active && this.section) {
      if (active.dataset.bg) this.section.style.setProperty('--rk-flavor-bg', active.dataset.bg);
      if (active.dataset.fg) this.section.style.setProperty('--rk-flavor-fg', active.dataset.fg);
    }

    // Anúncio do sabor para leitores de tela.
    if (active && this.live && active.dataset.flavor) {
      this.live.textContent = active.dataset.flavor;
    }

    // realIndex = índice do sabor (ignora os clones do loop).
    const ri = swiper.realIndex;

    // Dot ativo.
    this.dots.forEach((dot, i) => {
      const on = i === ri;
      dot.classList.toggle('is-active', on);
      dot.setAttribute('aria-selected', on ? 'true' : 'false');
    });

    // Card ativo: só o card do sabor centralizado aparece (largura cheia, abaixo).
    this.cards.forEach((card, i) => {
      const on = i === ri;
      card.classList.toggle('is-active', on);
      if (on) card.removeAttribute('aria-hidden');
      else card.setAttribute('aria-hidden', 'true');
    });
  }

  #goToDot(index) {
    const swiper = this._swiper;
    if (!swiper) return;
    if (typeof swiper.slideToLoop === 'function') swiper.slideToLoop(index);
    else swiper.slideTo(index);
  }

  teardown() {
    if (this._onInit) this.removeEventListener('rk:carousel:init', this._onInit);
    if (this._onClick) this.removeEventListener('click', this._onClick);
    if (this._swiper && this._onSlideChange) {
      try {
        this._swiper.off('slideChange', this._onSlideChange);
      } catch (e) {
        /* swiper já destruído pelo próprio carousel */
      }
    }
    this.classList.remove('is-ready');
    this._bound = false;
    this._swiper = null;
  }
}

if (!customElements.get('rk-flavor-showcase')) {
  customElements.define('rk-flavor-showcase', RkFlavorShowcase);
}
